import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { renderMarkdown } from "../lib/markdown";
import {
  getPage,
  listVersions,
  createVersion,
  updateVersionContent,
  submitVersion,
  approveVersion,
  commitVersion,
  rejectVersion,
  PageRow,
  PageVersionRow,
} from "../lib/api";
import { useSpaceContext } from "./SpaceLayout";
import { computeDiff, DiffLine } from "../lib/diff";

type PageDetail = { published_version: PageVersionRow | null } & PageRow;

const ACTIVE_STATUSES = ["draft", "pending_review", "approved"];

export default function PageEditPage({ pageSlug: pageSlugProp }: { pageSlug?: string }) {
  const { username, spaceSlug, pageSlug: pageSlugParam } = useParams<{
    username: string;
    spaceSlug: string;
    pageSlug: string;
  }>();
  const pageSlug = pageSlugProp ?? pageSlugParam ?? "";
  const navigate = useNavigate();
  const { space, isOwner } = useSpaceContext();
  const isAutoMode = space?.policy?.approval_mode === "auto";

  const [page, setPage] = useState<PageDetail | null>(null);
  const [activeDraft, setActiveDraft] = useState<PageVersionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [content, setContent] = useState("");
  const [commitMessage, setCommitMessage] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [compareMode, setCompareMode] = useState(false);

  const loadData = useCallback(() => {
    if (!username || !spaceSlug || !pageSlug) return;
    setLoading(true);
    setError(null);

    getPage(username, spaceSlug, pageSlug)
      .then((pageData) =>
        listVersions(pageData.id).then((versions) => ({ pageData, versions }))
      )
      .then(({ pageData, versions }) => {
        setPage(pageData);
        const active = versions.find((v) =>
          ACTIVE_STATUSES.includes(v.status)
        ) ?? null;
        setActiveDraft(active);
        if (active) {
          setContent(active.content);
        } else if (pageData.published_version) {
          setContent(pageData.published_version.content);
        }
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Failed to load page");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [username, spaceSlug, pageSlug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const renderedHtml = content ? renderMarkdown(content) : "";
  const publishedHtml = page?.published_version?.content
    ? renderMarkdown(page.published_version.content)
    : "";

  // Creates a draft if none exists, or updates the existing one
  const handleSaveDraft = async () => {
    if (!page) return;
    setSaving(true);
    setActionError(null);
    try {
      let version;
      if (activeDraft && activeDraft.status === "draft") {
        version = await updateVersionContent(activeDraft.id, {
          content,
          commit_message: commitMessage || undefined,
        });
      } else {
        version = await createVersion(page.id, {
          content,
          commit_message: commitMessage || undefined,
          parent_version_id: page.published_version?.id,
        });
      }
      setActiveDraft(version);
      setCommitMessage("");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!page) return;
    setSaving(true);
    setActionError(null);
    try {
      // If no draft exists yet, create one first then submit
      let draft = activeDraft;
      if (!draft || draft.status !== "draft") {
        draft = await createVersion(page.id, {
          content,
          commit_message: commitMessage || undefined,
          parent_version_id: page.published_version?.id,
        });
      }
      const version = await submitVersion(draft.id);
      if (version.status === "published") {
        // Auto-publish mode: redirect to page view
        navigate(`/${username}/${spaceSlug}/${pageSlug}`);
      } else {
        setActiveDraft(version);
      }
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to submit");
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!activeDraft) return;
    setSaving(true);
    setActionError(null);
    try {
      const version = await approveVersion(activeDraft.id);
      setActiveDraft(version);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to approve");
    } finally {
      setSaving(false);
    }
  };

  const handleCommit = async () => {
    if (!activeDraft) return;
    setSaving(true);
    setActionError(null);
    try {
      await commitVersion(activeDraft.id);
      navigate(`/${username}/${spaceSlug}/${pageSlug}`);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to publish");
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!activeDraft) return;
    setSaving(true);
    setActionError(null);
    try {
      const version = await rejectVersion(
        activeDraft.id,
        rejectReason || undefined
      );
      setActiveDraft(version);
      setShowRejectInput(false);
      setRejectReason("");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to reject");
    } finally {
      setSaving(false);
    }
  };

  const status = activeDraft?.status ?? null;

  const statusBadgeClass = (s: string) => {
    switch (s) {
      case "draft":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "pending_review":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "approved":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "published":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Sub-nav: page title + actions */}
        <div className="flex items-center gap-3 mb-6 text-sm text-gray-500 dark:text-gray-400">
          <Link
            to={`/${username}/${spaceSlug}/${pageSlug}`}
            className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            {page ? page.title : pageSlug}
          </Link>
          <span>/</span>
          <span className="font-medium text-gray-700 dark:text-gray-300">Edit</span>
          <div className="ml-auto flex items-center gap-3">
            {page?.published_version && (
              <button
                onClick={() => setCompareMode((v) => !v)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  compareMode
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-400"
                }`}
              >
                {compareMode ? "Hide published" : "Compare with published"}
              </button>
            )}
            <Link
              to={`/${username}/${spaceSlug}/${pageSlug}/history`}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              History
            </Link>
          </div>
        </div>
        {loading && (
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
            <div className="h-96 bg-gray-100 dark:bg-gray-800 rounded" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && page && (
          <>
            {/* Page title and status */}
            <div className="mb-6 flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {page.title}
                </h1>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  /{page.slug}
                </p>
              </div>
              {status && (
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusBadgeClass(status)}`}
                >
                  {status.replace("_", " ")}
                </span>
              )}
            </div>

            {actionError && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
                {actionError}
              </div>
            )}

            {/* Pending review: inline diff view */}
            {(status === "pending_review" || status === "approved") && page?.published_version && (() => {
              const diffLines = computeDiff(page.published_version.content, content);
              const added = diffLines.filter((l) => l.type === "added").length;
              const removed = diffLines.filter((l) => l.type === "removed").length;
              return (
                <div className="mb-6 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 text-xs border-b border-gray-200 dark:border-gray-800">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Changes vs published</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-mono">+{added}</span>
                    <span className="text-red-500 dark:text-red-400 font-mono">−{removed}</span>
                    <span className="text-gray-400 dark:text-gray-500">{diffLines.filter(l => l.type === "unchanged").length} unchanged</span>
                  </div>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <pre className="text-xs font-mono leading-5">
                      {diffLines.map((line: DiffLine, li: number) => (
                        <div key={li} className={
                          line.type === "added"
                            ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 px-4"
                            : line.type === "removed"
                            ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 line-through opacity-70"
                            : "text-gray-600 dark:text-gray-400 px-4"
                        }>
                          <span className="select-none mr-2 opacity-40">
                            {line.type === "added" ? "+" : line.type === "removed" ? "−" : " "}
                          </span>
                          {line.text || " "}
                        </div>
                      ))}
                    </pre>
                  </div>
                </div>
              );
            })()}

            {/* Editor / Preview 2-column layout (only when editable) */}
            {status !== "pending_review" && status !== "approved" && (
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Editor column */}
              <div className="flex flex-col gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Markdown
                </label>
                <textarea
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-3 text-sm font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ minHeight: "400px" }}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your content in Markdown..."
                />
              </div>

              {/* Preview column */}
              <div className="flex flex-col gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {compareMode ? "Published version" : "Preview"}
                </label>
                <div
                  className={`prose dark:prose-invert max-w-none border rounded-lg p-4 overflow-auto ${
                    compareMode
                      ? "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10"
                      : "border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900"
                  }`}
                  style={{ minHeight: "400px" }}
                  dangerouslySetInnerHTML={{ __html: compareMode ? publishedHtml : renderedHtml }}
                />
                {compareMode && !page?.published_version && (
                  <p className="text-xs text-gray-400 dark:text-gray-600">No published version yet.</p>
                )}
              </div>
            </div>
            )}

            {/* Commit message */}
            {(status === "draft" || status === null) && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Commit message (optional)
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={commitMessage}
                  onChange={(e) => setCommitMessage(e.target.value)}
                  placeholder="Describe your changes..."
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* No active draft OR draft exists: same buttons */}
              {(status === null || status === "draft") && (
                <>
                  <button
                    onClick={handleSaveDraft}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {saving ? "Saving..." : "Save Draft"}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className={`px-4 py-2 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors ${
                      isOwner && isAutoMode
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {saving
                      ? isOwner && isAutoMode ? "Publishing..." : "Submitting..."
                      : isOwner && isAutoMode ? "Publish" : "Submit for Review"}
                  </button>
                </>
              )}

              {/* Pending review status */}
              {status === "pending_review" && (
                <>
                  <button
                    onClick={handleApprove}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {saving ? "Approving..." : "Approve"}
                  </button>
                  {!showRejectInput ? (
                    <button
                      onClick={() => setShowRejectInput(true)}
                      disabled={saving}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Reject
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        className="border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reason (optional)"
                      />
                      <button
                        onClick={handleReject}
                        disabled={saving}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {saving ? "Rejecting..." : "Confirm Reject"}
                      </button>
                      <button
                        onClick={() => {
                          setShowRejectInput(false);
                          setRejectReason("");
                        }}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Approved status */}
              {status === "approved" && (
                <button
                  onClick={handleCommit}
                  disabled={saving}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {saving ? "Publishing..." : "Publish"}
                </button>
              )}

              {/* Published status: start a new draft */}
              {status === "published" && (
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {saving ? "Creating..." : "Edit (New Draft)"}
                </button>
              )}

              <Link
                to={`/${username}/${spaceSlug}/${pageSlug}`}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
